'use client';

import { useCallback, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import type { VariantsEntity } from '@eliadmin/core/src/api/products/types/product-types';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useComparisonStore } from '@eliadmin/core/src/stores/comparison-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { normalizeProductDescriptionHtml } from '@/utils/product-description';
import { Button } from '../../ui/button';
import { Paragraph } from '../../ui/paragraph';
import { ComparisonProduct, type ComparisonVariantChange } from './comparison-product';

/** Fixed widths keep every row aligned with the product columns while the table scrolls. */
const COLUMN = 'w-[240px] shrink-0 sm:w-[280px] lg:w-[332px]';
const COLUMNS_GAP = 'gap-4 sm:gap-5 lg:gap-6';

type ComparedProduct = { product: Product; variant?: VariantsEntity };
type ComparisonRow = { key: string; label: string; values: string[] };

const BASE_ROWS: {
  key: string;
  label: string;
  value: (item: ComparedProduct) => string | null | undefined;
}[] = [
  {
    key: 'manufacturer',
    label: 'Výrobca',
    value: ({ product }) => product.manufacturers?.map(m => m.name).join(', '),
  },
  {
    key: 'availability',
    label: 'Dostupnosť',
    value: ({ product, variant }) => (variant?.availability ?? product.availability)?.name,
  },
  {
    key: 'code',
    label: 'Kód produktu',
    value: ({ product, variant }) => variant?.code ?? product.code,
  },
];

/** Variant values when a variant is selected, otherwise the product's own attributes. */
function getParameters({ product, variant }: ComparedProduct): [string, string][] {
  const parameters: [string, string][] = variant?.values?.length
    ? variant.values.map(value => [value.attribute_name ?? '', value.name])
    : (product.attributes ?? []).map(attribute => [
        attribute.name,
        (attribute.values ?? []).map(value => value.name).join(', '),
      ]);
  return parameters.filter(([label, value]) => label && value);
}

function buildRows(items: ComparedProduct[]): ComparisonRow[] {
  const base = BASE_ROWS.map(row => ({
    key: row.key,
    label: row.label,
    values: items.map(item => row.value(item)?.trim() ?? ''),
  }));

  const parameters = items.map(item => new Map(getParameters(item)));
  // Union of all parameter names, in the order they first appear.
  const labels = [...new Set(parameters.flatMap(map => [...map.keys()]))];
  const parameterRows = labels.map(label => ({
    key: `parameter-${label}`,
    label,
    values: parameters.map(map => map.get(label) ?? ''),
  }));

  return [...base, ...parameterRows].filter(row => row.values.some(Boolean));
}

function RowLabel({ children }: { children: string }) {
  return (
    <Paragraph size="base" color="muted-foreground" className="sticky left-0 w-fit font-semibold">
      {children}
    </Paragraph>
  );
}

export function ComparisonProducts() {
  const products = useComparisonStore(s => s.products);
  const clearAll = useComparisonStore(s => s.clearAll);
  const locale = useLocaleStore(s => s.currentLocale);
  const { me, isInitialized } = useCustomerStore();
  const [variants, setVariants] = useState<Record<number, VariantsEntity | undefined>>({});
  const [descriptionsOpen, setDescriptionsOpen] = useState(false);

  // Stored products are card snapshots; prices, availability and parameters come from the detail.
  const details = useQueries({
    queries: products.map(stored => ({
      queryKey: [CacheKey.PRODUCT_DETAIL, locale, stored.id, me?.authToken ?? null],
      queryFn: () =>
        fetchSlugDetail({ slug: stored.slug, locale, authToken: me?.authToken })
          .then(res => res['App\\Models\\Product'] as unknown as Product)
          .catch(() => null),
      enabled: isInitialized,
    })),
  });

  const handleVariantChange = useCallback<ComparisonVariantChange>((productId, variant) => {
    setVariants(current =>
      current[productId] === variant ? current : { ...current, [productId]: variant },
    );
  }, []);

  if (!products.length) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-6 pr-5 xl:pr-0">
        <Paragraph className="w-full text-center">
          Žiadne produkty na porovnanie. Pridajte produkty kliknutím na ikonu porovnania pri
          produkte.
        </Paragraph>
        <Button asChild>
          <Link prefetch={false} href={ROUTES.ESHOP()}>
            Prejsť do e-shopu
          </Link>
        </Button>
      </div>
    );
  }

  const items = products.map((stored, index) => {
    const product = details[index]?.data ?? stored;
    return { product, isFull: !!details[index]?.data, variant: variants[product.id] };
  });
  const rows = buildRows(items);
  const descriptions = items.map(({ product, variant }) =>
    normalizeProductDescriptionHtml(variant?.description || product.description),
  );
  const hasDescriptions = descriptions.some(Boolean);

  return (
    <div className="flex w-full flex-col items-center gap-6 sm:gap-8">
      <div
        className={cn('custom-container', 'flex items-center justify-between gap-4 pr-5 xl:pr-0')}
      >
        <Paragraph color="muted-foreground">
          {products.length === 1
            ? 'Pridajte ďalší produkt, aby ste ich mohli porovnať.'
            : `Porovnávate ${products.length} ${products.length < 5 ? 'produkty' : 'produktov'}`}
        </Paragraph>
        <Button variant="white" className="shrink-0 border border-border" onClick={clearAll}>
          Vymazať všetko
        </Button>
      </div>

      <div className={cn('custom-container', 'overflow-x-auto pb-4')}>
        <div className="flex w-max min-w-full flex-col">
          <div className={cn('flex items-stretch pb-6 sm:pb-8', COLUMNS_GAP)}>
            {items.map(({ product, isFull }) => (
              <div key={product.id} className={COLUMN}>
                <ComparisonProduct
                  product={product}
                  isFull={isFull}
                  onVariantChange={handleVariantChange}
                />
              </div>
            ))}
          </div>

          {rows.map(row => (
            <div key={row.key} className="flex flex-col gap-2 border-t border-border py-3 sm:py-4">
              <RowLabel>{row.label}</RowLabel>
              <div className={cn('flex', COLUMNS_GAP)}>
                {row.values.map((value, index) => (
                  <Paragraph key={items[index].product.id} className={cn(COLUMN, 'break-words')}>
                    {value || '—'}
                  </Paragraph>
                ))}
              </div>
            </div>
          ))}

          {hasDescriptions && (
            <div className="flex flex-col gap-3 border-t border-border py-3 sm:py-4">
              <RowLabel>Popis a parametre</RowLabel>
              <div className={cn('flex items-start', COLUMNS_GAP)}>
                {descriptions.map((description, index) => (
                  <div
                    key={items[index].product.id}
                    className={cn(
                      COLUMN,
                      'relative overflow-hidden',
                      !descriptionsOpen && 'max-h-[480px]',
                    )}
                  >
                    {description ? (
                      <Paragraph
                        size="base"
                        color="muted-foreground"
                        innerHtml={description}
                        className={cn(
                          'w-full break-words [overflow-wrap:anywhere]',
                          '[&_:is(h1,h2,h3,h4,h5,h6)]:text-base [&_:is(h1,h2,h3,h4,h5,h6)]:font-semibold',
                          '[&_img]:h-auto [&_img]:max-w-full [&_table]:!w-full [&_ul]:ml-5 [&_ul]:list-disc',
                        )}
                      />
                    ) : (
                      <Paragraph>—</Paragraph>
                    )}
                    {!descriptionsOpen && (
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-white/0" />
                    )}
                  </div>
                ))}
              </div>
              <Button
                variant="white"
                className="sticky left-0 w-fit border border-border"
                aria-expanded={descriptionsOpen}
                onClick={() => setDescriptionsOpen(open => !open)}
              >
                {descriptionsOpen ? 'Skryť popis' : 'Zobraziť celý popis'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
